// 選択中の付箋・テキスト図形の本文を「URL のリンク」へ変換する計画を組み立てる純粋関数。
//
// canvas-stage.tsx には単体テストが無いため、選択からの計画組み立てをここへ切り出し、
// canvas-stage 側は「store から読む・dispatcher を呼ぶ・トーストを出す」だけにする。

import {
  isHttpUrl,
  linkifyMarkdownRange,
  planPlainLineLinkReplacement,
  textBaseStyleOf,
  TEXT_BODY_MAX_LENGTH,
  type CanvasObject,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";

export interface LinkifyTextUpdate {
  readonly objectId: string;
  readonly text: string;
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
}

export interface LinkifySelectionPlan {
  /** そのまま dispatcher.updateTexts へ渡せる更新一覧。 */
  readonly updates: readonly LinkifyTextUpdate[];
  /** 対象だったが本文長上限を超えるため見送った件数。 */
  readonly skippedTooLong: number;
  /** 選択に含まれていた未ロックの付箋・テキスト図形の件数（0 なら対象外の選択）。 */
  readonly candidateCount: number;
}

/** 対象外の選択（従来の OGP リンクカード経路へ落とす）を表す空プラン。 */
const EMPTY_PLAN: LinkifySelectionPlan = { candidateCount: 0, skippedTooLong: 0, updates: [] };

/**
 * 選択中のオブジェクトのうち、未ロックの付箋・テキスト図形の本文を URL のリンクへ変換する
 * 計画を返す。`candidateCount === 0` のときは呼び出し側が従来の貼り付け経路へ委ねる。
 */
export function planLinkifySelectedObjects(params: {
  readonly objects: Readonly<Record<string, CanvasObject>>;
  readonly selectedObjectIds: readonly string[];
  readonly url: string;
}): LinkifySelectionPlan {
  const { objects, selectedObjectIds, url } = params;
  // isLikelyPasteUrl（正規表現）だけを通る入力（https://[ 等）は、ここで対象外へ落として
  // 従来の OGP リンクカード経路に委ねる（「何も起きない」状態を作らない）。
  if (!isHttpUrl(url)) return EMPTY_PLAN;

  const updates: LinkifyTextUpdate[] = [];
  let candidateCount = 0;
  let skippedTooLong = 0;
  for (const objectId of selectedObjectIds) {
    const current = objects[objectId];
    if (!current) continue;
    if (current.type !== "sticky" && current.type !== "text" && current.type !== "section")
      continue;
    if (current.locked === true) continue;
    candidateCount += 1;
    if (current.type === "section") {
      const plan = planPlainLineLinkReplacement({
        end: current.text.length,
        maxLength: TEXT_BODY_MAX_LENGTH,
        start: 0,
        text: current.text,
        url,
      });
      switch (plan.kind) {
        case "replace":
          updates.push({
            objectId,
            text: current.text.slice(0, plan.start) + plan.insert + current.text.slice(plan.end),
          });
          break;
        case "no-visible-text":
          updates.push({ objectId, text: url });
          break;
        case "too-long":
          skippedTooLong += 1;
          break;
        case "invalid-url":
          return EMPTY_PLAN;
        case "multiline":
        case "needs-snap":
        case "unchanged":
          // 候補には数えるが、壊れた記法や不要な op は作らない。
          break;
      }
      continue;
    }
    const outcome = linkifyMarkdownRange({
      end: current.text.length,
      maxLength: TEXT_BODY_MAX_LENGTH,
      start: 0,
      text: current.text,
      url,
      ...(current.type === "text"
        ? { base: textBaseStyleOf(current), runs: current.inlineStyles }
        : {}),
    });
    switch (outcome.kind) {
      case "linkified":
        updates.push({
          objectId,
          text: outcome.text,
          // テキスト図形では run が空でも変化が無くても必ず添える（§11.4.1）。
          // `inlineStyles` を省くと op へ `expectedTextStyleRevision` が載らず、
          // 他参加者が同じ図形へ範囲書式を確定した直後でも本文を無条件に置き換えてしまう。
          // 通常の本文編集は空配列でも送って同じ競合境界を検証している。
          ...(current.type === "text" ? { inlineStyles: outcome.runs } : {}),
        });
        break;
      case "no-visible-text":
        // 可視文字が無い本文（空・空白のみ・箇条書きマーカーのみ等）は URL 文字列で置き換える。
        // 本文をすべて入れ替えるため旧 run は残せない。
        updates.push({
          objectId,
          text: url,
          ...(current.type === "text" ? { inlineStyles: [] } : {}),
        });
        break;
      case "too-long":
        skippedTooLong += 1;
        break;
      case "unchanged":
        // すでに同じ URL のリンク。op を送らない。
        break;
      default:
        // invalid-url は先頭の isHttpUrl で弾いているため到達しない。防御的に対象外へ落とす。
        return EMPTY_PLAN;
    }
  }
  return { candidateCount, skippedTooLong, updates };
}
